import { getAudioContext, isAudioRunning, resumeAudioContext } from './audioContext'
import { BIRTH_TIMING } from './birthTimeline'
import { RESTART_TIMING } from './restartRenderer'
export type BirthAudioEvent = 'heart-burst' | 'baby' | 'frame'

export type BirthAudio = {
  sync: (elapsed: number) => void
  syncRestart: (elapsed: number) => void
  dispose: () => void
}


const NO_AUDIO: BirthAudio = {
  sync: () => undefined,
  syncRestart: () => undefined,
  dispose: () => undefined
}

/** The sound layer is optional; every Web Audio operation is best effort. */
export function createBirthAudio(reducedMotion: boolean): BirthAudio {
  if (reducedMotion) return NO_AUDIO
  const context = getAudioContext()
  if (!context) return NO_AUDIO
  let master: GainNode | null = null
  try {
    master = context.createGain()
    master.gain.value = 0.45
    master.connect(context.destination)
    // Autoplay policy may reject this. It must never become an unhandled error.
    void resumeAudioContext(context)
  } catch {
    try { master?.disconnect() } catch { /* best effort */ }
    return NO_AUDIO
  }

  const activeSources = new Set<OscillatorNode>()
  const activeGains = new Set<GainNode>()
  const fired = new Set<BirthAudioEvent>()
  let disposed = false

  const release = (source: OscillatorNode, gain: GainNode) => {
    activeSources.delete(source)
    activeGains.delete(gain)
    try { source.disconnect() } catch { /* context may already be closed */ }
    try { gain.disconnect() } catch { /* context may already be closed */ }
  }

  const tone = (frequency: number, duration: number, volume: number, type: OscillatorType = 'sine', endFrequency?: number, delay = 0) => {
    if (disposed || !context || !master) return
    try {
      const source = context.createOscillator()
      const gain = context.createGain()
      const start = context.currentTime + delay
      const end = start + duration
      source.type = type
      source.frequency.setValueAtTime(frequency, start)
      if (endFrequency !== undefined) source.frequency.exponentialRampToValueAtTime(Math.max(1, endFrequency), end)
      gain.gain.setValueAtTime(0.0001, start)
      gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume), start + Math.min(0.025, duration / 5))
      gain.gain.exponentialRampToValueAtTime(0.0001, end)
      source.connect(gain)
      gain.connect(master)
      activeSources.add(source)
      activeGains.add(gain)
      source.onended = () => release(source, gain)
      source.start(start)
      source.stop(end + 0.02)
    } catch {
      // A blocked or partial implementation must not break the visual intro.
    }
  }

  const play = (event: BirthAudioEvent) => {
    if (disposed || fired.has(event)) return
    fired.add(event)
    switch (event) {
      case 'heart-burst':
        tone(180, .18, .07, 'triangle', 65)
        break
      case 'baby':
        ;[659, 784, 988].forEach((frequency, index) => tone(frequency, .15, .14, 'triangle', undefined, index * .09))
        break
      case 'frame':
        tone(90, .2, .18, 'square', 55)
        tone(58, .28, .14, 'sine', 30, .03)
        break
    }
  }

  let lastHeartbeat = -1
  const sync = (elapsed: number) => {
    if (disposed || !isAudioRunning(context) || document.hidden) return
    if (elapsed < BIRTH_TIMING.heartbeatEnd) {
      const beat = Math.floor(elapsed / 420)
      if (beat !== lastHeartbeat) {
        lastHeartbeat = beat
        tone(116, .12, .08, 'sine', 84)
        tone(142, .16, .06, 'sine', 96, .15)
      }
    } else if (elapsed < BIRTH_TIMING.heartBurstEnd) play('heart-burst')
    else if (elapsed < BIRTH_TIMING.babyEnd) play('baby')
    else if (elapsed < BIRTH_TIMING.frameEnd) play('frame')
  }

  const dispose = () => {
    if (disposed) return
    disposed = true
    for (const source of activeSources) {
      try { source.stop() } catch { /* already ended */ }
      try { source.disconnect() } catch { /* best effort */ }
    }
    for (const gain of activeGains) {
      try { gain.disconnect() } catch { /* best effort */ }
    }
    activeSources.clear()
    activeGains.clear()
    try { master?.disconnect() } catch { /* best effort */ }
    // The shared context belongs to the app; only release this intro mixer.
  }

  let served = false
  let swept = false
  let slapped = false
  let bankrupt = false
  let memoryBeat = -1
  const syncRestart = (elapsed: number) => {
    if (disposed || !isAudioRunning(context) || document.hidden) return
    if (elapsed >= RESTART_TIMING.soupServe && !served) {
      served = true
      tone(310, .18, .035, 'sine', 520)
    }
    if (elapsed >= RESTART_TIMING.soupSweep && !swept) {
      swept = true
      tone(220, .12, .15, 'sawtooth', 45)
      tone(1100, .18, .055, 'triangle', 180)
    }
    if (elapsed >= RESTART_TIMING.soupImpact && !slapped) {
      slapped = true
      tone(92, .26, .14, 'sine', 28)
      tone(760, .12, .055, 'sawtooth', 100)
    }
    if(elapsed >= RESTART_TIMING.cutImpact && !bankrupt) {
      bankrupt = true
      tone(440,.45,.08,'sawtooth',30)
      tone(70,.35,.12,'sine',25)
    }
    const beat = Math.floor((elapsed - RESTART_TIMING.memoriesStart) / 750)
    if (beat >= 0 && beat !== memoryBeat) {
      memoryBeat = beat
      tone(330 + (beat % 4) * 110, .2, .045, 'sine')
    }
  }
  return { sync, syncRestart, dispose }
}
